import { CSSProperties, useCallback, ReactElement } from 'react'
import { styled, Typography } from '@mui/material'
import { RawFileMessage } from '@weblite/ts-action/message'
import { getFileByUrl, getFileUrl, openFile } from '@weblite/file'
import { ActionHandler } from '$common/action-handler'
import { FileMessageIcon } from '$common/icons/file-message'
import { useContentFileStatus } from '$common/react-hooks/message'
import {
  convertExtensionToMimeType,
  formatFileSize,
  getFileExtension,
  getFileTitle,
} from '$func/file'
import { IS_IOS, isInWebView } from '$func/environmentInfo'
import { sendEventToTwa } from '$logic/twaCommunication/twaCommunication.api'
import { TWA_EVENTS } from '$logic/twaCommunication/twaCommunication.types'
import { TWA_API_SUPPORT } from '$logic/twaCommunication'
import { MessageMenuIcon } from '../../components/menu-icon'

const Container = styled('div')`
  display: flex;
  position: relative;
  align-items: center;

  padding: 10px 10px 10px 32px;
  box-sizing: border-box;
  overflow: hidden;
`

const TextsContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const FileMetaTypography = styled(Typography)({
  display: 'inline-block',
})

export function File({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  iid,
  roomId,
  fromMe,
  id,
}: {
  attachment: RawFileMessage['attachment']
  statusComp: ReactElement | null
  uploadId: string
  rootStyle?: CSSProperties
  iid: number
  roomId: string
  fromMe: boolean
  id: string
}) {
  const { fileId, filename, size } = attachment

  const { state, percentage, uploadedBytes, cancelOperation, startDownload } =
    useContentFileStatus({
      fileId,
      uploadId,
      messageType: 'FILE',
      messageId: id,
      filename,
    })

  const isInProgress = state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } =
    state === 'UPLOADING'
      ? formatFileSize(uploadedBytes)
      : formatFileSize(
          Math.round(size * (percentage / 100)),
          fileSizeUnit,
          false,
        )

  const fileTitle = getFileTitle(filename)
  const fileExtension = getFileExtension(filename).toUpperCase()

  const onOpenFile = useCallback(() => {
    if (TWA_API_SUPPORT.OPEN_FILE && isInWebView)
      AndroidBridge?.runFile(
        fileId,
        getFileUrl(fileId),
        filename,
        convertExtensionToMimeType(fileExtension),
      )
    else openFile(getFileUrl(fileId), filename, IS_IOS)
  }, [fileId, filename])

  return (
    <>
      <Container sx={{ width: '280px', ...rootStyle }}>
        <ActionHandler
          state={state}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={startDownload}
          onCustomActionIconClick={onOpenFile}
          customActionIconComp={
            <FileMessageIcon
              sx={{
                color: fromMe ? 'secondary500' : 'white',
                width: '24px',
                height: '24px',
              }}
            />
          }
          fromMe={fromMe}
        />

        <TextsContainer>
          <Typography
            color={fromMe ? 'neutral000' : 'neutral900'}
            variant="caption1"
            sx={{
              display: 'block',
              marginBottom: '8px',
            }}
            noWrap
          >
            {fileTitle}
          </Typography>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <FileMetaTypography
              variant="caption1"
              color={fromMe ? 'neutral200' : 'neutral500'}
            >
              {fileExtension}
            </FileMetaTypography>

            <FileMetaTypography
              variant="caption1"
              color={fromMe ? 'neutral400' : 'neutral300'}
              sx={{
                margin: '0 4px',
              }}
            >
              /
            </FileMetaTypography>

            <FileMetaTypography
              variant="caption1"
              noWrap
              sx={{
                color: isInProgress
                  ? 'neutral200'
                  : fromMe
                  ? 'neutral200'
                  : 'neutral500',
              }}
            >
              {isInProgress
                ? `${progressedSizeText} از ${fileSizeText}`
                : fileSizeText}
            </FileMetaTypography>
          </div>
        </TextsContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} fromMe={fromMe} />
      </Container>
      {statusComp}
    </>
  )
}

File.defaultProps = {
  rootStyle: null,
}
